import { AppPaths } from "@p0security/shared/routes/constants";
import {
  Col,
  Collapse,
  Flex,
  Modal,
  Row,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { GraphTooltip } from "components/GraphTooltip";
import { getCurrentToken } from "components/Login/tokens/TokenProvider";
import { useFlow } from "components/Welcome/hooks/useFlow";
import { BaselineDiv } from "components/divs";
import { DOCS_URLS } from "constants/docsUrls";
import { doc, getDoc } from "firebase/firestore";
import { validate as validateEmail } from "isemail";
import { jwtDecode } from "jwt-decode";
import { useFlags } from "launchdarkly-react-client-sdk";
import { compact } from "lodash";
import React, {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { handleErrorWithToastMessage } from "utils/error";
import { z } from "zod";

import {
  DB,
  useFirestoreCollection,
  useFirestoreDoc,
} from "../../providers/FirestoreProvider";
import { useAuthFetch, useUser } from "../Login/hook";
import { NormalizedUser } from "../Login/types/auth";
import { Tenant, UserRole } from "../Login/util";
import { TagInput } from "../TagInput";
import { ApiKeysPanel } from "./ApiKeysPanel";
import { ApprovalRequirements } from "./ApprovalRequirements";

const { Title } = Typography;

const StyledTagInput = styled(TagInput)`
  margin-top: 0.5em;
`;

const REMOVE_OWN_ACCESS_ERROR =
  "You can't remove yourself as an owner. Instead, add another member of your organization, then have them remove you.";

/**
 * Schema for the Firebase token payload
 */
const firebaseTokenSchema = z.object({
  firebase: z.object({
    sign_in_attributes: z
      .object({
        groups: z.array(z.string()).optional(),
      })
      .optional(),
  }),
});

interface EmailDiff {
  added: string[];
  removed: string[];
  warnings: {
    outOfDomain: string[];
    invalidEmails: string[];
    count: number;
    multi: boolean;
  };
}

const emailDiff = (
  oldEmails: string[],
  newEmails: string[],
  emailRegex: RegExp
): EmailDiff => {
  const old = new Set(oldEmails);
  const new_ = new Set(newEmails);

  const added = Array.from(new_.difference(old).values());
  const removed = Array.from(old.difference(new_).values());

  const invalidEmails = added.filter((email) => !validateEmail(email));
  // Avoid double-warning on emails that are already an `invalidEmail`
  const outOfDomain = added.filter(
    (email) => validateEmail(email) && !emailRegex.test(email)
  );

  return {
    added,
    removed,
    warnings: {
      outOfDomain,
      invalidEmails,
      get count(): number {
        return this.outOfDomain.length + this.invalidEmails.length;
      },
      get multi(): boolean {
        return (
          [this.outOfDomain.length, this.invalidEmails.length].reduce(
            (prev, cur) => prev + (cur > 0 ? 1 : 0),
            0
          ) > 1
        );
      },
    },
  };
};

const DEFAULT_TITLE = "Email Confirmation";

const orgDomainMessage = (arr: string[]): string =>
  arr.length > 1
    ? "These emails do not match your organization's domain."
    : "This email does not match your organization's domain.";

const invalidEmailMessage = (arr: string[]): string =>
  arr.length > 1 ? "These emails are not valid." : "This email is not valid.";

const inferTitle = (diff?: EmailDiff | null): string => {
  if (!diff || !diff.warnings.count || diff.warnings.multi) {
    return DEFAULT_TITLE;
  }

  const { outOfDomain, invalidEmails } = diff.warnings;
  return outOfDomain.length
    ? orgDomainMessage(outOfDomain)
    : invalidEmails.length
      ? invalidEmailMessage(invalidEmails)
      : DEFAULT_TITLE;
};

const MemberEditConfirmModal: React.FC<
  ComponentProps<typeof Modal> & {
    stagedDiff?: EmailDiff;
  }
> = ({ stagedDiff, ...rest }) => {
  const { warnings } = stagedDiff ?? {};

  return (
    <Modal
      title={inferTitle(stagedDiff)}
      closable={{ "aria-label": "Custom Close Button" }}
      open={!!stagedDiff}
      okText={
        (stagedDiff?.warnings.count ?? 1) > 1
          ? "Yes, Add Emails"
          : "Yes, Add Email"
      }
      {...rest}
    >
      {warnings === undefined ? (
        "Something is wrong, are you sure you want to add these emails?"
      ) : (
        <>
          <p>
            {warnings.count > 1
              ? "Are you sure you want to add them?"
              : "Are you sure you want to add it?"}
          </p>
          {warnings.outOfDomain.length > 0 && (
            <>
              {warnings.multi && (
                <p>{orgDomainMessage(warnings.outOfDomain)}</p>
              )}
              <Flex gap="small" wrap>
                {warnings.outOfDomain.map((m) => (
                  <Tag key={m}>{m}</Tag>
                ))}
              </Flex>
            </>
          )}

          {warnings.invalidEmails.length > 0 && (
            <>
              {warnings.multi && (
                <p>{invalidEmailMessage(warnings.invalidEmails)}</p>
              )}

              <Flex gap="small" wrap>
                {warnings.invalidEmails.map((m) => (
                  <Tag key={m}>{m}</Tag>
                ))}
              </Flex>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

// @IMPORTANT!!!
// If you need to make changes to this Settings UI, you should also
// make the same changes to the SettingsSandbox page to keep them
// relatively in sync.

// @TODO: Separate the model/controller side of this component
// from the view side. That way we can have a layer that switches
// the data between sandbox and non-sandbox and keep the same
// view code.
const MembersEdit: React.FC<{ role: UserRole }> = ({ role }) => {
  const tenantId = useContext(Tenant);
  const { user } = useUser();
  const authFetch = useAuthFetch(handleErrorWithToastMessage);
  const derivedRole = useMemo(
    () => role.replace("approver", "manager"),
    [role]
  );
  const tenantPath = useMemo(() => `o/${tenantId}`, [tenantId]);
  const { doc: tenantDoc } = useFirestoreDoc<
    { openIdDomain: string } | undefined
  >(tenantPath, {
    live: true,
  });
  const openIdDomain = tenantDoc?.data?.openIdDomain;

  const path = useMemo(
    () => `o/${tenantId}/roles/${derivedRole}/bindings`,
    [derivedRole, tenantId]
  );
  const { docs: membersDocs } = useFirestoreCollection<{ email: string }>(
    path,
    {
      live: true,
    }
  );
  const members = useMemo(
    () => membersDocs?.map((m) => m.data.email ?? m.id) ?? [],
    [membersDocs]
  );

  const updateMembership = useCallback(
    (method: "DELETE" | "PUT", email: string) =>
      authFetch(
        `settings/roles/${derivedRole}/bindings/users/${encodeURIComponent(
          email.toLowerCase()
        )}`,
        { method }
      ),
    [authFetch, derivedRole]
  );

  const applyDiff = useCallback(
    async ({ added, removed }: Pick<EmailDiff, "added" | "removed">) => {
      for (const email of added) {
        const response = await updateMembership("PUT", email);
        if (response) message.success(`Added ${email}`);
      }
      for (const email of removed) {
        const response = await updateMembership("DELETE", email);
        if (response) message.success(`Removed ${email}`);
      }
    },
    [updateMembership]
  );

  const [stagedDiff, setStagedDiff] = useState<EmailDiff | undefined>(
    undefined
  );

  const onChange = useCallback(
    async (newMembers: string[]) => {
      const diff = emailDiff(
        members,
        newMembers,
        new RegExp(openIdDomain ?? ".*")
      );

      if (!diff.warnings.count) {
        await applyDiff(diff);
        return;
      }

      setStagedDiff(diff);
    },
    [members, applyDiff, openIdDomain, setStagedDiff]
  );

  const onModalOk = useCallback(async () => {
    if (stagedDiff) await applyDiff(stagedDiff);
    setStagedDiff(undefined);
  }, [stagedDiff, applyDiff, setStagedDiff]);

  const onModalCancel = useCallback(() => {
    setStagedDiff(undefined);
  }, [setStagedDiff]);

  return openIdDomain === undefined || membersDocs === undefined ? (
    <Spin />
  ) : (
    <>
      <MemberEditConfirmModal
        onOk={onModalOk}
        onCancel={onModalCancel}
        stagedDiff={stagedDiff}
      />
      <BaselineDiv>
        <div>Users:</div>
        <SettingsTagInput
          user={user}
          role={role}
          tags={members}
          onChange={onChange}
        />
      </BaselineDiv>
    </>
  );
};

const SettingsTagInput: React.FC<{
  tags: string[];
  role: UserRole;
  user?: NormalizedUser;
  onChange: (tags: string[]) => void;
}> = ({ tags, role, user, onChange }) => {
  const tagToElement = useCallback(
    (tag: string, onClose: () => void) => {
      const canClose = role !== "owner" || tag !== user?.email;
      const key = tag;
      const tagElem = (
        <Tag key={key} closable={canClose} onClose={onClose}>
          {tag}
        </Tag>
      );
      return canClose ? (
        tagElem
      ) : (
        <GraphTooltip key={key} title={REMOVE_OWN_ACCESS_ERROR}>
          {tagElem}
        </GraphTooltip>
      );
    },
    [role, user]
  );

  return (
    <StyledTagInput
      placeholder="Enter an email address"
      tags={tags}
      // Superset pattern from https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
      tagPattern={/[a-zA-Z0-9:.!#$%&'*+/=?^_`{|}~\-@]/}
      tagToElement={tagToElement}
      onUpdate={onChange}
      key={role}
    />
  );
};

/** Edits groups that can access a role */
const GroupsEdit: React.FC<{ role: UserRole }> = ({ role }) => {
  const tenantId = useContext(Tenant);
  const { user } = useUser();
  const authFetch = useAuthFetch(handleErrorWithToastMessage);
  const derivedRole = useMemo(
    () => role.replace("approver", "manager"),
    [role]
  );
  const path = useMemo(
    () => `o/${tenantId}/roles/${derivedRole}`,
    [derivedRole, tenantId]
  );
  const roleData = useFirestoreDoc<{ allowed?: { groups: string[] } }>(path, {
    live: true,
  });
  const groups = useMemo(
    () => roleData.doc?.data.allowed?.groups ?? [],
    [roleData]
  );

  const [userGroups, setUserGroups] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    const fetchUserGroups = async () => {
      const token = await getCurrentToken();
      if (!token) return;
      const userJwtData = firebaseTokenSchema.parse(jwtDecode<unknown>(token));
      setUserGroups(userJwtData.firebase.sign_in_attributes?.groups);
    };
    fetchUserGroups();
  }, [user]);

  const updateGroupMembership = useCallback(
    (method: "DELETE" | "PUT", group: string) =>
      authFetch(
        `settings/roles/${derivedRole}/bindings/groups/${encodeURIComponent(
          group
        )}`,
        { method }
      ),
    [authFetch, derivedRole]
  );

  const saveGroups = useCallback(
    async (newGroups: string[]) => {
      if (!user || !userGroups) return;
      if (derivedRole === "owner") {
        // Prevent owners from removing the last binding that grants them ownership
        const userIsInGroups = newGroups.find((g) => userGroups?.includes(g));
        if (!userIsInGroups) {
          const userBindingExists = (
            await getDoc(doc(DB, `${path}/bindings/${user.email}`))
          ).exists();
          if (!userBindingExists) {
            // TODO: Prevent closing of last group tag
            message.error(REMOVE_OWN_ACCESS_ERROR);
            return;
          }
        }
      }

      const addedGroups = newGroups.filter((g) => !groups.includes(g));
      const removedGroups = groups.filter((g) => !newGroups.includes(g));

      for (const addedGroup of addedGroups) {
        const response = await updateGroupMembership("PUT", addedGroup);
        if (response) message.success(`Groups ${newGroups} added`);
      }
      for (const removedGroup of removedGroups) {
        const response = await updateGroupMembership("DELETE", removedGroup);
        if (response) message.success(`Groups ${newGroups} removed`);
      }
    },
    [derivedRole, groups, path, updateGroupMembership, user, userGroups]
  );

  // If user does not have a groups claim, this provider is not compatible with group access
  return !userGroups ? null : roleData === undefined ? (
    <Spin />
  ) : (
    <BaselineDiv>
      <div>Groups:</div>
      <StyledTagInput
        placeholder="Enter a group name"
        tags={groups}
        tagPattern={/[a-zA-Z0-9-_ ]/}
        onUpdate={saveGroups}
        key={role}
      />
    </BaselineDiv>
  );
};

export const Admin: React.FC = () => {
  const [flow] = useFlow();

  const flags = useFlags();
  return (
    <Collapse
      defaultActiveKey={["access_control", "jit", "apikeys"]}
      items={compact([
        {
          key: "access_control",
          label: "Access control",
          children: (
            <Row gutter={[0, 32]}>
              <Col span={24}>
                <Title level={4}>Owners</Title>
                Owners can add integrations and alter settings.
                <MembersEdit role="owner" />
                <GroupsEdit role="owner" />
              </Col>
              {/* Show all roles if now flow is passed */}
              {/* If one is passed, only show relevant roles */}
              {flow !== "assessment" && (
                <Col span={24}>
                  <Title level={4}>Security Reviewers</Title>
                  Security Reviewers can review routing rules and user access
                  (via P0 CLI), and optionally be configured as approvers for
                  access requests in {/* nosemgrep */}
                  <Link
                    to={
                      flow ? DOCS_URLS.JIT_ROUTING : `../${AppPaths.Policies}`
                    }
                    target={flow ? "_blank" : undefined}
                    rel={flow ? "noopener noreferrer" : undefined}
                  >
                    Routing Rules
                  </Link>
                  .
                  <MembersEdit role="approver" />
                  <GroupsEdit role="approver" />
                </Col>
              )}
              {flow !== "jit" && (
                <Col span={24}>
                  <Title level={4}>Assessment Users</Title>
                  Assessment Users can run, manage, and view IAM assessments.
                  <MembersEdit role="iamOwner" />
                  <GroupsEdit role="iamOwner" />
                </Col>
              )}
              {flow !== "jit" && (
                <Col span={24}>
                  <Title level={4}>Assessment Viewers</Title>
                  Assessment Viewers can view IAM assessments.
                  <MembersEdit role="iamViewer" />
                  <GroupsEdit role="iamViewer" />
                </Col>
              )}

              {
                /* This role is not used for either onboarding flow.
                  Currently only for Splunk while we are working with CX to evaluate the install validation
                  experience for other customers */
                !flow && flags.splunkInstallEmails && (
                  <Col span={24}>
                    <Title level={4}>Account Contacts</Title>
                    Account Contacts receive email notifications for this
                    account, such as invalid installation warnings.
                    <MembersEdit role="contact" />
                  </Col>
                )
              }
            </Row>
          ),
        },
        flow
          ? undefined
          : {
              key: "access_management",
              label: "Access Management",
              children: <ApprovalRequirements />,
            },
        flow
          ? undefined
          : { key: "apikeys", label: "API Keys", children: <ApiKeysPanel /> },
      ])}
    />
  );
};
